import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, CalendarCheck, Clock, Hospital, Phone } from "lucide-react";
import Seo from "../seo";
import { api, CATEGORY_MEDIA, TREATMENT_MEDIA, articleImage, CLINIC } from "../api";
import { Reveal, MaskedLines, Chapter, EASE } from "../components/motion";
import InstagramFeed from "../components/InstagramFeed";
import Reviews from "../components/Reviews";
import { useLanguage } from "../i18n";

const CONDITION_GROUPS = [
    { name: "Kidney & Urinary Tract", desc: "Stones, infection, blood in urine and urinary symptoms", to: "/conditions" },
    { name: "Prostate", desc: "Enlargement, inflammation and prostate cancer evaluation", to: "/conditions" },
    { name: "Male Urology", desc: "Fertility, varicocele and men's urological health", to: "/conditions" },
    { name: "Urethra & Reconstructive", desc: "Strictures and urethral reconstruction", to: "/conditions" },
    { name: "Urological Oncology", desc: "Kidney, bladder and prostate cancers", to: "/conditions" },
    { name: "Pediatric Urology", desc: "Urological conditions in infants and children", to: "/conditions" },
];

const MARQUEE_ITEMS = [
    "Kidney Stones", "Recurrent UTI", "Hematuria", "BPH", "Urethral Stricture",
    "Varicocele", "Nocturia", "Bladder Cancer", "Hydronephrosis", "Erectile Dysfunction",
    "Prostate Health", "Undescended Testis",
];

export default function Home() {
    const { t } = useLanguage();
    const [articles, setArticles] = useState([]);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

    useEffect(() => {
        api.get("/articles").then((r) => setArticles(r.data.articles.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div>
            <Seo title="Dr. Krishnakanth AVB | Consultant Urologist in Madurai"
                description="Dr. Krishnakanth AVB is a Consultant Urologist in Madurai providing evaluation and treatment of common urological conditions along with evidence-based patient education." />

            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden border-b border-border bg-gradient-to-b from-sky-50/80 via-white to-white" data-testid="hero-section">
                <div className="spotlight absolute inset-0" aria-hidden="true" />
                <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-16 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-10 lg:px-10 lg:pb-28 lg:pt-24">
                    <div>
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
                            className="font-mono-label text-steel">
                            Consultant Urologist — Madurai, Tamil Nadu
                        </motion.p>
                        <MaskedLines
                            className="mt-6 font-display text-4xl font-medium leading-[1.04] tracking-tight text-navy sm:text-5xl lg:text-6xl"
                            lines={["Dr. Krishnakanth AVB", "Consultant Urologist"]}
                            accentIndex={1} />
                        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.8, ease: EASE }}
                            className="mt-5 font-display text-xl font-medium tracking-tight text-steel sm:text-2xl">
                            MBBS, MS (General Surgery), MCh (Urology)
                        </motion.p>
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: EASE }}>
                            <div className="mt-8 max-w-xl">
                                <p className="text-base leading-relaxed text-steel md:text-lg">
                                    Urological care with evidence-based patient education.
                                </p>
                                <p className="mt-3 text-base leading-relaxed text-steel md:text-lg">
                                    Evaluation and management of urological conditions at{" "}<br className="hidden sm:block" />
                                    Preethi Multispeciality Hospital, Madurai.
                                </p>
                            </div>
                            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                                <Link to="/appointment" data-testid="hero-book-appointment-btn"
                                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent hover:shadow-lg">
                                    <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                                    {t("book_appointment")}
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                                </Link>
                                <Link to="/conditions" data-testid="hero-explore-conditions-btn"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-7 py-4 text-sm font-semibold text-navy transition-all duration-300 hover:border-accent hover:text-accent">
                                    Explore Urology Conditions
                                </Link>
                            </div>
                            <div className="mt-10 max-w-xl overflow-hidden rounded-2xl border border-accent/25 bg-gradient-to-br from-sky-50 to-blue-50/50 shadow-sm" data-testid="hero-clinic-card">
                                <div className="flex items-center gap-3 px-6 pt-5">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy text-white">
                                        <Hospital className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                    <p className="font-display text-xl font-semibold tracking-tight text-navy sm:text-2xl">
                                        Preethi Multispeciality Hospital
                                    </p>
                                </div>
                                <p className="px-6 pt-1 text-lg font-semibold text-navy sm:pl-20">Madurai, Tamil Nadu</p>
                                <div className="mt-4 space-y-1.5 px-6 pb-5">
                                    {CLINIC.opdSchedule.map((d) => (
                                        <p key={d.day} className="flex items-center justify-between gap-3 text-sm">
                                            <span className="flex items-center gap-2.5 font-semibold text-navy">
                                                <Clock className="h-4 w-4 text-accent" aria-hidden="true" />
                                                {d.day}
                                            </span>
                                            <span className={d.hours.includes("Closed") ? "font-medium text-slate-400" : "font-medium text-steel"}>
                                                {d.hours}
                                            </span>
                                        </p>
                                    ))}
                                    <p className="flex items-center gap-2.5 pt-2 text-sm font-semibold text-emerald-700">
                                        <Phone className="h-4 w-4" aria-hidden="true" />
                                        {CLINIC.emergency}
                                    </p>
                                </div>
                                <div className="h-1.5 bg-gradient-to-r from-accent via-sky-400 to-emerald-400" aria-hidden="true" />
                            </div>
                        </motion.div>
                    </div>
                    <motion.div style={{ y: imgY }} className="relative mx-auto w-full max-w-sm lg:max-w-md">
                        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1, ease: EASE }}
                            className="relative">
                            <div className="absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(37,99,235,0.10),rgba(14,165,233,0.05)_55%,transparent_72%)]" aria-hidden="true" />
                            <div className="arch-frame absolute inset-x-8 bottom-0 top-28 bg-gradient-to-b from-sky-100/80 to-blue-50/30" aria-hidden="true" />
                            <img src="/assets/portrait-cutout.png" alt="Dr. Krishnakanth AVB, Consultant Urologist" loading="eager" data-testid="hero-doctor-image"
                                className="relative w-full [mask-image:linear-gradient(to_bottom,black_88%,transparent_99%)]" />
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* PATIENT REVIEWS */}
            <Reviews />

            {/* INSTAGRAM VIDEOS */}
            <InstagramFeed />

            {/* MARQUEE */}
            <div className="border-b border-border bg-mist py-5" aria-hidden="true">
                <Marquee speed={28} gradient={false} pauseOnHover>
                    {MARQUEE_ITEMS.map((item) => (
                        <span key={item} className="mx-8 flex items-center gap-8 font-mono-label text-[0.68rem] text-steel">
                            {item} <span className="h-1 w-1 rounded-full bg-accent" />
                        </span>
                    ))}
                </Marquee>
            </div>

            {/* 01 / PROFILE */}
            <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28" data-testid="home-profile-section">
                <Chapter index={1} label="The Practice" />
                <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-20">
                    <Reveal>
                        <h2 className="font-display text-3xl font-medium leading-tight text-navy sm:text-4xl">
                            About Dr. Krishnakanth
                        </h2>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-steel">
                            Dr. Krishnakanth AVB is a Consultant Urologist providing evaluation and management of a broad
                            range of adult urological conditions, with an emphasis on evidence-based diagnosis, appropriate
                            treatment selection and patient education.
                        </p>
                        <Link to="/about" data-testid="read-more-about-btn"
                            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                            Read More About Dr. Krishnakanth
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <ul className="divide-y divide-border border-y border-border">
                            {[
                                ["Qualifications", "MBBS, MS (General Surgery), MCh (Urology)"],
                                ["Current position", "Consultant Urologist"],
                                ["Hospital affiliation", "Preethi Multispeciality Hospital, Madurai"],
                                ["Clinical interests", "Stone disease, prostate health, urethral reconstruction, men's health"],
                            ].map(([k, v]) => (
                                <li key={k} className="grid grid-cols-[140px_1fr] gap-4 py-5 text-sm">
                                    <span className="font-mono-label text-[0.62rem] pt-0.5 text-steel">{k}</span>
                                    <span className="text-navy leading-relaxed">{v}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* 02 / CONDITIONS */}
            <section className="border-y border-border bg-mist" data-testid="home-conditions-section">
                <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Chapter index={2} label="Conditions" />
                            <Reveal><h2 className="mt-8 max-w-xl font-display text-3xl font-medium leading-tight text-navy sm:text-4xl">
                                Urological conditions, explained clearly
                            </h2></Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <Link to="/conditions" data-testid="view-all-conditions-btn"
                                className="group inline-flex items-center gap-2 text-sm font-semibold text-accent">
                                All conditions <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {CONDITION_GROUPS.map((g, i) => {
                            const media = CATEGORY_MEDIA[g.name] || CATEGORY_MEDIA["Kidney & Urinary Tract"];
                            return (
                                <Reveal key={g.name} delay={i * 0.06} y={20}>
                                    <Link to={g.to} data-testid={`condition-group-${i}`}
                                        className="group block h-full overflow-hidden rounded-2xl border border-border bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-xl">
                                        <div className="relative h-40 overflow-hidden">
                                            <img src={media.img} alt={media.alt} loading="lazy"
                                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            <div className={`absolute inset-x-0 bottom-0 h-1.5 ${media.bar}`} aria-hidden="true" />
                                            <span className={`absolute left-5 top-5 rounded-full border px-3 py-1 font-mono-label text-[0.6rem] backdrop-blur-sm ${media.chip} bg-opacity-90`}>
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <div className="p-7">
                                            <h3 className="font-display text-xl font-medium text-navy transition-colors group-hover:text-accent">{g.name}</h3>
                                            <p className="mt-2 text-sm leading-relaxed text-steel">{g.desc}</p>
                                            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                                                Explore
                                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                            </span>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 03 / TREATMENTS */}
            <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28" data-testid="home-treatments-section">
                <Chapter index={3} label="Treatments & Procedures" />
                <div className="mt-10 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
                    <Reveal>
                        <h2 className="font-display text-3xl font-medium leading-tight text-navy sm:text-4xl">
                            Procedures explained before they are recommended
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-steel">
                            Every procedure page follows the same honest structure — what it is, when it is considered,
                            what it involves, recovery, risks and common questions. No procedure is presented as universally superior.
                        </p>
                        <Link to="/treatments" data-testid="explore-treatments-btn"
                            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                            Explore treatments <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <div className="relative mt-10 max-w-md overflow-hidden rounded-2xl border border-border">
                            <img src={TREATMENT_MEDIA["Laparoscopic Urology"].img} alt="Surgeon in an operating theatre" loading="lazy"
                                className="h-56 w-full object-cover" />
                            <div className="absolute inset-x-0 bottom-0 h-1.5 bg-teal-500" aria-hidden="true" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <ul className="divide-y divide-border border-y border-border">
                            {[
                                ["Stone Surgery", "RIRS · URSL · PCNL · ESWL", "/treatments/rirs"],
                                ["Prostate Procedures", "TURP · HoLEP · Medical therapy", "/treatments/turp"],
                                ["Urethral Reconstruction", "Dilatation · DVIU · Urethroplasty", "/treatments/urethroplasty"],
                                ["Bladder Procedures", "Cystoscopy · TURBT · Intravesical therapy", "/treatments/cystoscopy"],
                                ["Urological Oncology", "Kidney · Bladder · Prostate cancer care", "/treatments/kidney-cancer-surgery"],
                                ["Male Infertility", "Evaluation · Varicocele · Sperm retrieval", "/treatments/male-infertility-evaluation"],
                            ].map(([name, items, to]) => (
                                <li key={name}>
                                    <Link to={to} data-testid={`treatment-row-${name.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="group flex items-center justify-between gap-4 py-5">
                                        <div>
                                            <p className="font-display text-lg font-medium text-navy group-hover:text-accent transition-colors">{name}</p>
                                            <p className="mt-1 text-sm text-steel">{items}</p>
                                        </div>
                                        <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-300 transition-all duration-300 group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* 04 / PATIENT EDUCATION */}
            <section className="border-t border-border bg-navy text-white" data-testid="home-education-section">
                <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <span className="font-mono-label text-accent">04</span>
                            <span className="font-mono-label text-slate-400 ml-4">Patient Education</span>
                            <h2 className="mt-8 max-w-xl font-display text-3xl font-medium leading-tight sm:text-4xl">
                                Understand your condition, in plain language
                            </h2>
                        </div>
                        <Link to="/patient-education" data-testid="view-all-articles-btn"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-white">
                            All articles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </div>
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {articles.map((a, i) => (
                            <Reveal key={a.slug} delay={i * 0.08}>
                                <Link to={`/patient-education/${a.slug}`} data-testid={`home-article-${a.slug}`}
                                    className="group block overflow-hidden rounded-2xl border border-white/10 bg-navy-800 transition-colors duration-300 hover:border-accent/50">
                                    <div className="h-44 overflow-hidden">
                                        <img src={articleImage(a)} alt="" loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <div className="p-6">
                                        <p className="font-mono-label text-[0.6rem] text-slate-400">{a.category} · {a.reading_time} min read</p>
                                        <h3 className="mt-3 font-display text-lg font-medium leading-snug group-hover:text-accent transition-colors">{a.title}</h3>
                                        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-slate-300">{a.summary}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28" data-testid="home-cta-section">
                <Reveal className="relative overflow-hidden rounded-3xl border border-border bg-mist px-8 py-16 text-center lg:py-20">
                    <div className="spotlight absolute inset-0" aria-hidden="true" />
                    <h2 className="relative font-display text-3xl font-medium text-navy sm:text-4xl">Have a urological concern?</h2>
                    <p className="relative mx-auto mt-4 max-w-md text-base text-steel">
                        Book a consultation for a proper evaluation and a clear discussion of your options.
                    </p>
                    <Link to="/appointment" data-testid="cta-book-appointment-btn"
                        className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent hover:shadow-lg">
                        <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                        {t("book_appointment")}
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}
